import { differenceInCalendarDays, eachDay, format, subDays, parse, addMinutes, getDay } from 'date-fns'
import { isDate } from 'lodash'
import ja from 'date-fns/locale/ja'
import { padZero } from '@/utils/number'

/**
 * date-fnsのラッパー
 */
export const formatDate = (date, template = 'YYYY-MM-DD') => {
  if (!date) return null
  if (typeof date === 'string') {
    // date-fnsで受け付けられない型は、先に変換をかける
    date = relaceToHalf(date)
    date = formatToPadZero(date)
    if (isYearUndifinedDate(date)) return formatYearUndefinedDate(date, template)
  }

  // Invalid Dateは返却しない
  if (format(date) === 'Invalid Date') return null

  return format(date, template, { locale: ja })
}

// 全角英数記号は半角に変換する
const regex = /[Ａ-Ｚａ-ｚ０-９！＂＃＄％＆＇（）＊＋，－．／：；＜＝＞？＠［＼］＾＿｀｛｜｝]/g
export const relaceToHalf = str => {
  const newStr = str.replace(regex, value => String.fromCharCode(value.charCodeAt(0) - 0xfee0))

  return newStr
}

// 全角英数記号があるかどうか
export const hasFullWidth = str => !!str.match(regex)

/**
 * 日付を { year, month, day } なオブジェクトに分解します
 *
 * @param {string|date} date 日付
 */
export function splitDate(date) {
  if (!date) return { year: 0, month: 0, day: 0 }
  if (isDate(date)) return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() }
  const match = `${date}`.match(/^(\d*?)[/-]?(\d{1,2})[/-]?(\d{1,2})$/)
  if (!match) return { year: 0, month: 0, day: 0 }
  const [, year, month, day] = match
  return {
    year: +year || 0,
    month: +month || 0,
    day: +day || 0,
  }
}

/**
 * dateの型をYYYY-MM-DDに揃える
 * - safariでDate型を扱う際にYYYY-M-D形式だとInvalid Dateを返してしまうため
 */
const formatToPadZero = date => {
  const match = date.match(/^(\d{4})[/|-](\d{1,2})[/|-](\d{1,2})$/)
  if (match && match.length > 0) {
    return `${match[1]}-${padZero(match[2])}-${padZero(match[3])}`
  }
  return date
}

/**
 * YYYYのない形式の日付はdate-fnsを介さずformatする
 * - date-fnsはYYYYがない場合2001に補完してしまう仕様のため
 * - @param date 基本的に01-01の型を扱うが、-の他に/区切りには対応。
 * - @param template YYYY-M-D, YYYY-MM-DD, YYYY-mm-ddなどには対応。
 */
const formatYearUndefinedDate = (date, template = 'YYYY-MM-DD') => {
  const match = date.match(/^(\d{1,2})[/|-](\d{1,2})$/)
  if (!match || match.length === 0) return null

  if (template.match(/^[y|Y][y|Y][y|Y][y|Y]$/)) return 0
  if (template.match(/^[m|M][m|M]$/)) return padZero(match[1])
  if (template.match(/^[m|M]$/)) return `${Number(match[1])}`
  if (template.match(/^[d|D][d|D]$/)) return padZero(match[2])
  if (template.match(/^[d|D]$/)) return `${Number(match[2])}`

  // templateを分割
  const doubleDigit = template.match(/^.*([m|M][m|M])(.*)([d|D][d|D])(.*)$/)
  const singleDigit = template.match(/^.*([m|M])(.*)([d|D])(.*)$/)

  if (!doubleDigit && !singleDigit) return null

  let mSep = '-'
  if (doubleDigit && doubleDigit[1].match(/^[m|M][m|M]$/)) {
    mSep = doubleDigit[2]
  } else if (singleDigit && singleDigit[1].match(/^[m|M]$/)) {
    mSep = singleDigit[2]
  }

  let dSep = '-'
  if (doubleDigit && doubleDigit[3].match(/^[d|D][d|D]$/)) {
    dSep = doubleDigit[4]
  } else if (singleDigit && singleDigit[3].match(/^[d|D]$/)) {
    dSep = singleDigit[4]
  }

  const month = doubleDigit && doubleDigit[1].match(/^[m|M][m|M]$/) ? padZero(match[1]) : `${Number(match[1])}`
  const day = doubleDigit && doubleDigit[3].match(/^[d|D][d|D]$/) ? padZero(match[2]) : `${Number(match[2])}`
  return `${month}${mSep}${day}${dSep}`
}

const isYearUndifinedDate = date => date.match(/^\d{1,2}[/|-]\d{1,2}$/)

/** endTimeを取得する
 * @param hour: HH
 * @param minutes: mm
 * @param utilizationTime: 分。 (eq. 120)
 */
export const getEndTime = (hour, minute, utilizationTime = 0, separator = '：', limit = 0) => {
  let time = Number(hour * 60) + Number(minute) + Number(utilizationTime)
  if (limit > 0 && time > limit) time = limit
  return `${Math.floor(time / 60)}${separator}${padZero(time % 60)}`
}

/**
 * 30時制などの変則時制の日時に変換します
 *
 * @param {(string|Date)} date 変換する Date
 * @param {number} [clock=30] 時制
 * @return {object} 変換後の日付 YYYY-MM-DD と時間 hh:mm
 */
export function toHourClock(date, clock = 30) {
  date = parse(date)
  const is24Over = date.getHours() < clock - 24
  date = is24Over ? subDays(date, 1) : date
  const hour = date.getHours() + (is24Over ? 24 : 0)
  const minute = date.getMinutes()
  return {
    date: format(date, 'YYYY-MM-DD'),
    time: `${padZero(hour)}:${padZero(minute)}`,
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour,
    minute,
  }
}

/**
 * 日時のオブジェクトを Date に変換します
 *
 * @param {object} 日時のオブジェクト
 * @return {date} 変換後の Date
 */
export function toDate({ year, month, day, hour = 0, minute = 0 }) {
  return addMinutes(`${year}-${padZero(month)}-${padZero(day)}`, hour * 60 + +minute)
}

/**
 * 30時制などの変則時制から通常のDateに変換します
 *
 * @param {(string|number)} year 年
 * @param {(string|number)} month 月
 * @param {(string|number)} day 日
 * @param {(string|number)} [hour=0] 時
 * @param {(string|number)} [minute=0] 分
 * @returns {Date} 変換後のDate
 */
export function fromHourClock(year, month, day, hour = 0, minute = 0) {
  return toDate({ year, month, day, hour, minute })
}

/**
 * 本日の日付を返します
 *
 * @return {string} 本日の日付 YYYY-MM-DD
 */
export function today() {
  return toHourClock(new Date()).date
}

/**
 * 日付の配列から、連続する日付を範囲 { start, end } としてまとめます。
 *
 * @param {array} dates 日付の配列
 * @param {number=3} minDates まとめる始める連続する日数
 * @returns {arrray} 日付範囲の配列
 */
export function datesToRanges(dates = [], minDates = 3) {
  return dates
    .reduce((groups, date, i) => {
      if (i && differenceInCalendarDays(date, dates[i - 1]) === 1) {
        groups[groups.length - 1].push(date)
      } else {
        groups.push([date])
      }
      return groups
    }, [])
    .reduce(
      (ranges, group) =>
        ranges.concat(
          group.length >= minDates
            ? { start: group[0], end: group[group.length - 1] }
            : group.map(date => ({ start: date })),
        ),
      [],
    )
}

/**
 * 日付範囲 { start, end } の配列を、日付の配列に展開します。
 *
 * @param {array} ranges 日付範囲の配列
 * @returns {arrray} 日付の配列
 */
export function rangesToDates(ranges = []) {
  return ranges.reduce(
    (dates, { start, end }) => dates.concat(eachDay(start, end || start).map(date => format(date, 'YYYY-MM-DD'))),
    [],
  )
}

/**
 * 曜日を返します
 *
 * @param {Date | String | Number} 日付
 * @returns {String} 曜日
 */
export function weekDay(date) {
  return '日月火水木金土'[getDay(date)]
}

/**
 * APIのコード値から曜日を返します
 *
 * @param {Number} 曜日のコード値
 * @returns {String} 曜日
 */
export function dayOfWeek(day) {
  const list = { '1': '月', '2': '火', '3': '水', '4': '木', '5': '金', '6': '土', '0': '日', '-1': '祝', '-2': '祝前' }
  return list[day]
}
